import { useEffect, useState } from "react";
import { Plus, Copy, ExternalLink, Trash2, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";

const FEEDBACK_COLORS = {
  interested: "bg-emerald-100 text-emerald-700",
  reject: "bg-rose-100 text-rose-700",
  hold: "bg-amber-100 text-amber-700",
  pending: "bg-slate-100 text-slate-600",
};

export default function AdminShortlists() {
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [viewing, setViewing] = useState(null);

  const load = () => api.get("/admin/shortlists").then((r) => setItems(r.data.items || []));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this shortlist?")) return;
    await api.delete(`/admin/shortlists/${id}`); load();
  };

  const copyLink = (token) => {
    const url = `${window.location.origin}/shortlist/${token}`;
    navigator.clipboard.writeText(url);
    toast.success("Portal link copied");
  };

  return (
    <div className="space-y-6" data-testid="admin-shortlists">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900">Client Shortlist Portals</h1>
          <p className="text-sm text-slate-500">Generate signed links your clients can use to review shortlists — feedback auto-syncs back here</p>
        </div>
        <Button onClick={() => setCreating(true)} data-testid="shortlist-new"><Plus className="mr-1 h-4 w-4" />New shortlist</Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {items.map((s) => (
          <div key={s.id} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`shortlist-${s.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-heading text-base font-semibold text-slate-900">{s.name}</div>
                <div className="text-xs text-slate-500">{s.client_name} · {s.job_title}</div>
              </div>
              <Badge className="gap-1 bg-primary/10 text-primary"><Users className="h-3 w-3" />{s.application_ids?.length || 0}</Badge>
            </div>
            <div className="mt-3 flex gap-1">
              {["interested", "reject", "hold", "pending"].map((k) => {
                const n = (s.feedback || []).filter((f) => f.status === k).length;
                if (n === 0) return null;
                return <Badge key={k} className={`${FEEDBACK_COLORS[k]}`}>{n} {k}</Badge>;
              })}
            </div>
            <div className="mt-4 flex items-center gap-1 border-t border-slate-100 pt-3">
              <Button variant="outline" size="sm" onClick={() => copyLink(s.token)} data-testid={`shortlist-copy-${s.id}`}><Copy className="mr-1 h-3.5 w-3.5" />Copy link</Button>
              <a href={`/shortlist/${s.token}`} target="_blank" rel="noreferrer"><Button variant="outline" size="sm"><ExternalLink className="h-3.5 w-3.5" /></Button></a>
              <Button variant="outline" size="sm" onClick={() => setViewing(s)} data-testid={`shortlist-view-${s.id}`}>Feedback</Button>
              <div className="flex-1" />
              <Button variant="ghost" size="sm" onClick={() => remove(s.id)}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="col-span-full rounded-xl border border-dashed border-slate-300 p-12 text-center text-slate-400">No shortlists yet.</div>}
      </div>

      {creating && <CreateDialog onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
      {viewing && <FeedbackDialog s={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}

function CreateDialog({ onClose, onSaved }) {
  const [apps, setApps] = useState([]);
  const [clients, setClients] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [f, setF] = useState({ name: "", client_id: "", client_name: "", job_title: "" });

  useEffect(() => {
    api.get("/admin/applications", { params: { page_size: 300, status: "shortlisted" } }).then((r) => setApps(r.data.items || []));
    api.get("/admin/clients").then((r) => setClients(r.data.items || []));
  }, []);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const save = async () => {
    if (!f.name || selected.size === 0) { toast.error("Add a name and select at least 1 candidate"); return; }
    try {
      await api.post("/admin/shortlists", { ...f, application_ids: Array.from(selected) });
      toast.success("Shortlist created"); onSaved();
    } catch { toast.error("Failed"); }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Create client shortlist</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="Shortlist name (e.g. Formulation Scientist Batch 1)" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="shortlist-name" />
            <Input placeholder="Role" value={f.job_title} onChange={(e) => setF({ ...f, job_title: e.target.value })} />
          </div>
          <Select value={f.client_id} onValueChange={(v) => { const c = clients.find((x) => x.id === v); setF({ ...f, client_id: v, client_name: c?.name || "" }); }}>
            <SelectTrigger><SelectValue placeholder="Client" /></SelectTrigger>
            <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
          <div className="max-h-72 overflow-y-auto rounded-lg border border-slate-200 bg-white">
            <div className="border-b bg-slate-50 px-3 py-2 text-xs uppercase tracking-widest text-slate-500">
              Shortlisted candidates ({selected.size}/{apps.length} selected)
            </div>
            {apps.length === 0 && <div className="p-8 text-center text-sm text-slate-400">No shortlisted applications yet — move candidates to "shortlisted" status first.</div>}
            {apps.map((a) => (
              <label key={a.id} className="flex cursor-pointer items-center gap-3 border-b px-3 py-2 text-sm last:border-0 hover:bg-slate-50">
                <Checkbox checked={selected.has(a.id)} onCheckedChange={() => toggle(a.id)} data-testid={`shortlist-select-${a.id}`} />
                <div className="flex-1">
                  <div className="font-medium text-slate-800">{a.candidate_name}</div>
                  <div className="text-xs text-slate-500">{a.job_title} · {a.experience_years} yr · {a.location || "—"}</div>
                </div>
              </label>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} data-testid="shortlist-save">Create portal</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FeedbackDialog({ s, onClose }) {
  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Client feedback — {s.name}</DialogTitle></DialogHeader>
        <div className="space-y-2">
          {(s.feedback || []).map((f) => (
            <div key={f.application_id} className="rounded-lg border border-slate-200 bg-white p-3 text-sm">
              <div className="flex items-center justify-between">
                <div className="font-mono text-xs text-slate-500">{f.application_id.slice(0, 8)}…</div>
                <Badge className={FEEDBACK_COLORS[f.status] || FEEDBACK_COLORS.pending}>{f.status}</Badge>
              </div>
              {f.comment && <div className="mt-2 text-slate-700">{f.comment}</div>}
              {f.at && <div className="mt-1 text-xs text-slate-400">Updated {new Date(f.at).toLocaleString()}</div>}
            </div>
          ))}
          {(s.feedback || []).length === 0 && <div className="p-4 text-center text-sm text-slate-400">No feedback yet.</div>}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
